import { RouterLink } from '@gc-bridge/components/RouterLink';
import { LinkLabel } from '@gc-bridge/components/Section/LinkLabel';
import { Language } from '@mui/icons-material';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  styled,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '8.5px',
    padding: '10px',
  },
});

/**
 * Props for the ChooseRegionDialog component.
 *
 * @param regionUrls - A record mapping region codes to their URLs (e.g., `{ na: "https://...", emea: "https://..." }`)
 * @param regionNames - An optional record mapping region codes to display names (e.g., `{ na: "North America" }`).
 *   If a region code is not found in this map, the code itself is used as the display name.
 * @param onClose - Callback invoked when the dialog is closed
 * @param shouldRedirect - When true, region links open in a new tab
 */
interface Props {
  onClose: () => void;
  regionUrls: Record<string, string>;
  regionNames?: Record<string, string>;
  shouldRedirect?: boolean;
}

export function ChooseRegionDialog({
  onClose,
  regionUrls,
  regionNames,
  shouldRedirect,
}: Props) {
  const links = useMemo(
    () =>
      Object.entries(regionUrls).map(([code, url]) => ({
        link: url,
        label: regionNames?.[code] ?? code,
      })),
    [regionUrls, regionNames]
  );

  return (
    <StyledDialog open onClose={onClose} sx={{ borderRadius: '8.5px' }}>
      <DialogTitle sx={{ display: 'flex', gap: '10px', paddingLeft: '30px' }}>
        <Language
          sx={{
            color: 'primary.main',
            width: '24px',
            height: '24px',
          }}
        />
        <Typography variant="h6" color="text.primary">
          Choose Region
        </Typography>
      </DialogTitle>
      <DialogContent>
        {links.map(({ link, label }) => (
          <Link
            key={link}
            color="primary"
            component={RouterLink}
            to={link}
            target={shouldRedirect ? '_blank' : undefined}
            sx={{ textDecoration: 'none' }}
          >
            <LinkLabel label={label} uppercase background="background.paper" />
          </Link>
        ))}
      </DialogContent>
    </StyledDialog>
  );
}
